/**
 * `Wszystkie · Biblioteka · Twoje`, with a count beside each.
 *
 * A real radio group: a `<fieldset>` named by an sr-only `<legend>`, three native inputs off
 * screen, and the labels drawing the options. One tab stop for the group and the arrows walking it,
 * which the platform gives for free and a row of `<button>`s would have to be taught.
 *
 * ── All three figures, whichever list is drawn ──────────────────────────────────────────────
 * The switch is the one control on this screen that describes the two lists the trainer is NOT
 * looking at, so it holds all three counts — over the whole catalogue, never the filtered view.
 * They are hidden rather than absent while the read is open, so nothing moves when it lands.
 */

import { useTranslation } from 'react-i18next';

import { EXERCISE_SCOPES, type ExerciseScope, type ExerciseScopeCounts } from '../model/exerciseScope';

import hidden from '~/shared/ui/visuallyHidden.module.css';
import rule from '~/shared/ui/tabRule.module.css';
import styles from './ExerciseScopeSwitch.module.css';

interface ExerciseScopeSwitchProps {
  readonly scope: ExerciseScope;
  readonly onScopeChange: (scope: ExerciseScope) => void;
  /** `null` while the catalogue is still on its way or after it failed. */
  readonly counts: ExerciseScopeCounts | null;
}

export function ExerciseScopeSwitch({ scope, onScopeChange, counts }: ExerciseScopeSwitchProps) {
  const { t } = useTranslation();

  return (
    <fieldset className={styles.track}>
      {/* The group is NAMED for assistive technology and drawn by the options themselves. */}
      <legend className={hidden.srOnly}>{t('web:exerciseLibrary.scope.legend')}</legend>
      {EXERCISE_SCOPES.map((option) => {
        const selected = option === scope;
        const count = counts?.[option] ?? null;
        return (
          <label
            key={option}
            className={`${String(styles.option)} ${selected ? String(styles.optionSelected) : ''}`}
          >
            <input
              type="radio"
              name="exercise-library-scope"
              className={styles.input}
              value={option}
              checked={selected}
              onChange={() => {
                onScopeChange(option);
              }}
            />
            {t(`web:exerciseLibrary.scope.${option}`)}
            {/* ALWAYS rendered, and hidden rather than absent while the figure is unknown. See the
              * stylesheet: the reserve is three digits, because the catalogue is 477 movements. */}
            <span className={`${String(styles.count)} ${count === null ? String(styles.countWaiting) : ''}`}>
              {count === null ? null : <b className={styles.countFigure}>{count}</b>}
            </span>
            {/* The app's one tab underline — `shared/ui/tabRule.module.css` for the treatment and
              * the movement, this stylesheet's `.optionRule` for the box. The chosen state is a
              * second class on the same element, never a descendant of `.optionSelected`, so the
              * shared module depends on no class it does not own. */}
            <i
              className={`${String(rule.rule)} ${String(styles.optionRule)} ${
                selected ? String(rule.ruleOn) : ''
              }`}
              aria-hidden="true"
            />
          </label>
        );
      })}
    </fieldset>
  );
}
